import React from 'react';

import OhsEnvUtils from 'global-components/env/OhsEnvUtils';

import { OhsCusval, OhsCusvalType } from './OhsCusvalModel';
import OhsCheckboxField from '../fields/OhsCheckboxField';
import OhsCurrencyField from '../fields/OhsCurrencyField';
import OhsDateField from '../fields/OhsDateField';
import OhsEmailField from '../fields/OhsEmailField';
import OhsPhoneNumberField from '../fields/OhsPhoneNumberField';
import OhsRadioField from '../fields/OhsRadioField';
import OhsTextField from '../fields/OhsTextField';
import OhsUrlField from '../fields/OhsUrlField';
import OhsTimeField from '../fields/OhsTimeField';
import { OhsFormSettings } from '../services/OhsFormModels';
import OhsCusvalSelectField from './select/OhsCusvalSelectField';
import OhsStaticView from '../fields/OhsStaticView';
import OhsColorPicker from '../fields/OhsColorPicker';
import OhsCusvalLinkValueSysField from '../fields/linkHrContractor/OhsCusvalLinkValueSysField';
import OhsTreeSelectField from './select/OhsTreeSelectField';
import { getCusvalsOptionsDepth } from './OhsCusvalUtils';

function getCusvalField({
  sectionId,
  cusval,
  ohsFormSettings,
  templateId,
  recordId,
  maxCount,
}: {
  sectionId: string;
  cusval: OhsCusval;
  ohsFormSettings: OhsFormSettings;
  templateId?: string;
  recordId?: string;
  maxCount?: number;
}) {
  const newId = `${sectionId}.${cusval._id}`;
  switch (cusval.type) {
    case OhsCusvalType.Text:
    case OhsCusvalType.Number:
    case OhsCusvalType.Textarea:
      return (
        <OhsTextField
          key={newId}
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          type={cusval.type}
          ohsFormSettings={ohsFormSettings}
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    case OhsCusvalType.Color:
      return (
        <OhsColorPicker
          key={newId}
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          ohsFormSettings={ohsFormSettings}
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    case OhsCusvalType.Time:
      return (
        <OhsTimeField
          key={newId}
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          ohsFormSettings={ohsFormSettings}
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    case OhsCusvalType.Date:
      return (
        <OhsDateField
          key={newId}
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          ohsFormSettings={ohsFormSettings}
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    case OhsCusvalType.Email:
      return (
        <OhsEmailField
          key={newId}
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          ohsFormSettings={ohsFormSettings}
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    case OhsCusvalType.Url:
      return (
        <OhsUrlField
          key={newId}
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          ohsFormSettings={ohsFormSettings}
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    case OhsCusvalType.Telephone:
      return (
        <OhsPhoneNumberField
          key={newId}
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          ohsFormSettings={ohsFormSettings}
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    case OhsCusvalType.Currency:
      return (
        <OhsCurrencyField
          key={newId}
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          options={cusval.options ?? []}
          ohsFormSettings={ohsFormSettings}
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    case OhsCusvalType.Radio:
      return (
        <OhsRadioField
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          ohsFormSettings={ohsFormSettings}
          options={
            cusval.options?.map((item) => {
              return {
                name: item.name,
                value: item.name,
              };
            }) ?? []
          }
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    case OhsCusvalType.Checkbox:
      return (
        <OhsCheckboxField
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          ohsFormSettings={ohsFormSettings}
          options={
            cusval.options?.map((item) => {
              return {
                name: item.name,
                value: item.name,
                icon: item?.icon ?? '',
              };
            }) ?? []
          }
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    case OhsCusvalType.MutipleSelect: {
      const optionsDepth = getCusvalsOptionsDepth(cusval.options);

      const canUseTree = OhsEnvUtils.hasPermissionByEnv({
        showInDev: true,
        showInProd: false,
        showInMobileDev: true,
        showInMobileProd: false,
      });

      const FieldComponent =
        canUseTree && optionsDepth > 1 ? OhsTreeSelectField : OhsCusvalSelectField;

      return (
        <FieldComponent
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          ohsFormSettings={ohsFormSettings}
          options={cusval.options ?? []}
          multi
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    }

    case OhsCusvalType.Select:
      return (
        <OhsCusvalSelectField
          title={cusval.title}
          id={newId}
          description={cusval.description}
          required={cusval.required}
          ohsFormSettings={ohsFormSettings}
          options={cusval.options ?? []}
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
        />
      );
    case OhsCusvalType.LinkedValue:
      return (
        <OhsCusvalLinkValueSysField
          title={cusval.title}
          id={newId}
          fieldId={cusval._id}
          description={cusval.description}
          required={cusval.required}
          ohsFormSettings={ohsFormSettings}
          disabled={cusval.disabled}
          renderAction={cusval.renderAction}
          templateId={templateId || ''}
          recordId={recordId || ''}
          moduleType={cusval.moduleType || ''}
          maxCount={maxCount}
        />
      );
    case OhsCusvalType.Static:
      return <OhsStaticView id={newId} title={cusval.title} description={cusval.description} />;
    default:
      return <div />;
    // throw new Error('Unknown text');
  }
}
interface Props {
  sectionId: string;
  cusval: OhsCusval;
  ohsFormSettings: OhsFormSettings;
  templateId?: string;
  recordId?: string;
  maxCount?: number;
}
export default function OhsCusvalFieldFactory(props: Props) {
  return getCusvalField(props);
}
